import React, { useMemo } from 'react';
import * as fileIconAssets from '@ismart/file-icons';
import { icons, resizeSvg } from '@ismart/file-icons';

const VENDOR_SCALES: Record<string, number> = { microsoft: 1.15, office: 1.15 };

type GetIconOptions = {
  uniqueIds?: boolean;
  idPrefix?: string;
  scale?: number;
};

let idSeq = 0;

const namespaceIds = (svg: string, prefix?: string): string => {
  const resolvedPrefix = prefix ?? `i${++idSeq}_`;
  const ids: string[] = [];
  svg.replace(/\bid="([^"]+)"/g, (_, id) => {
    ids.push(id);
    return '';
  });
  if (!ids.length) return svg;

  let result = svg;
  ids.forEach((id) => {
    const nid = `${resolvedPrefix}${id}`;
    result = result
      .split(`id="${id}"`)
      .join(`id="${nid}"`)
      .split(`url(#${id})`)
      .join(`url(#${nid})`)
      .split(`href="#${id}"`)
      .join(`href="#${nid}"`)
      .split(`xlink:href="#${id}"`)
      .join(`xlink:href="#${nid}"`);
  });
  return result;
};

const getIcon = (
  vendor: string,
  name: string,
  size?: number,
  options?: GetIconOptions,
): string | null => {
  const key = `icon_${vendor}_${name.replace(/-/g, '_')}`;
  const raw = (fileIconAssets as Record<string, unknown>)[key];
  if (typeof raw !== 'string') return null;
  let svg = raw;

  const uniqueIds = !options || options.uniqueIds !== false;
  if (uniqueIds) {
    svg = namespaceIds(svg, options?.idPrefix);
  }

  const scale =
    options?.scale != null
      ? options.scale
      : VENDOR_SCALES[vendor];
  if (scale != null && scale !== 1) {
    svg = svg.replace(/viewBox="([^"]+)"/, (_, vb) => {
      const parts = vb.trim().split(/[\s,]+/);
      const x = parseFloat(parts[0]);
      const y = parseFloat(parts[1]);
      const w = parseFloat(parts[2]);
      const h = parseFloat(parts[3]);
      const nw = Math.round((w / scale) * 100) / 100;
      const nh = Math.round((h / scale) * 100) / 100;
      const nx = Math.round((x + (w - nw) / 2) * 100) / 100;
      const ny = Math.round((y + (h - nh) / 2) * 100) / 100;
      return `viewBox="${nx} ${ny} ${nw} ${nh}"`;
    });
    if (!svg.includes('overflow=')) {
      svg = svg.replace('<svg', '<svg overflow="visible"');
    }
  }

  return size != null ? resizeSvg(svg, size) : svg;
};

const EXTENSION_ALIASES: Record<string, string> = {
  doc: 'docx',
  dot: 'dotx',
  xls: 'xlsx',
  xlt: 'xltx',
  ppt: 'pptx',
  pps: 'ppsx',
  pot: 'potx',
  jpg: 'photo',
  jpeg: 'photo',
  png: 'photo',
  gif: 'photo',
  webp: 'photo',
  bmp: 'photo',
  svg: 'photo',
  tif: 'photo',
  tiff: 'photo',
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  mkv: 'video',
  webm: 'video',
  mp3: 'audio',
  wav: 'audio',
  m4a: 'audio',
  htm: 'html',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  vsd: 'vsdx',
  msg: 'email',
  eml: 'email',
};

const resolveMicrosoftIconName = (
  extension: string,
  contentType?: string | null,
): string => {
  const ext = extension.toLowerCase();

  if (icons.microsoft.includes(ext)) {
    return ext;
  }

  if (EXTENSION_ALIASES[ext] && icons.microsoft.includes(EXTENSION_ALIASES[ext])) {
    return EXTENSION_ALIASES[ext];
  }

  const type = contentType?.toLowerCase() || '';
  if (type.startsWith('image/')) return 'photo';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type.includes('pdf')) return 'pdf';
  if (type.includes('spreadsheet') || type.includes('excel')) return 'xlsx';
  if (type.includes('word')) return 'docx';
  if (type.includes('powerpoint') || type.includes('presentation')) return 'pptx';
  if (type.includes('zip') || type.includes('compressed')) return 'archive';

  return 'genericfile';
};

export const getMicrosoftIconSvg = (
  iconName: string,
  idPrefix?: string,
  size = 24,
): string => {
  const iconOptions = idPrefix ? { idPrefix, uniqueIds: true } : { uniqueIds: true };
  return getIcon('microsoft', iconName, size, iconOptions) || '';
};

export const getFileIconSvg = (
  fileName: string,
  contentType?: string | null,
  idPrefix?: string,
  size = 24,
): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  const iconOptions = idPrefix ? { idPrefix, uniqueIds: true } : { uniqueIds: true };

  const microsoftName = resolveMicrosoftIconName(extension, contentType);
  let svg = getIcon('microsoft', microsoftName, size, iconOptions);

  if (!svg && extension && icons.autodesk.includes(extension)) {
    svg = getIcon('autodesk', extension, size, iconOptions);
  }

  if (!svg) {
    svg = getIcon('microsoft', 'genericfile', size, iconOptions);
  }

  return svg || '';
};

type Props = {
  fileName?: string;
  icon?: string;
  contentType?: string | null;
  size?: number;
  idPrefix?: string;
  className?: string;
};

const FileTypeIcon = ({
  fileName,
  icon,
  contentType,
  size = 24,
  idPrefix,
  className,
}: Props) => {
  const svg = useMemo(() => {
    if (icon) {
      return getMicrosoftIconSvg(icon, idPrefix, size);
    }
    if (fileName) {
      return getFileIconSvg(fileName, contentType, idPrefix, size);
    }
    return '';
  }, [fileName, icon, contentType, size, idPrefix]);

  if (!svg) {
    return (
      <span
        className={className}
        style={{
          width: size,
          height: size,
          minWidth: size,
          flexShrink: 0,
          display: 'inline-block',
        }}
        aria-hidden
      />
    );
  }

  return (
    <span
      className={className}
      style={{
        width: size,
        height: size,
        minWidth: size,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        overflow: 'visible',
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
      aria-hidden
    />
  );
};

export default FileTypeIcon;
